import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { X, Share2, Star } from "lucide-react";
import { API } from "@/contexts/AuthContext";
import ReviewModal from "@/components/ReviewModal";

const SHARE_URL = "https://tuto.kosmosai.com.br";
const WHATSAPP_TEXT = encodeURIComponent(
  `Estou usando o TUTO — plataforma de gestão processual para advogados. Monitora processos e intimações automaticamente. Dá uma olhada: ${SHARE_URL}`
);

export default function NudgeCard() {
  const [nudge, setNudge] = useState(null); // "share" | "review" | null
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showReview, setShowReview] = useState(false);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/nudge`, { withCredentials: true })
      .then(res => {
        if (!active) return;
        const { nudges } = res.data;
        if (nudges.share) setNudge("share");
        else if (nudges.review) setNudge("review");
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (nudge) {
      const t = setTimeout(() => setVisible(true), 3000);
      return () => clearTimeout(t);
    }
  }, [nudge]);

  const dismiss = useCallback(async () => {
    setVisible(false);
    try {
      await axios.post(`${API}/nudge/dismiss`, { type: nudge }, { withCredentials: true });
    } catch {}
  }, [nudge]);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const handleReviewOpen = () => {
    setVisible(false);
    setShowReview(true);
  };

  const handleReviewClose = () => {
    setShowReview(false);
    // Dismiss registrado pelo POST /nudge/review no submit; se fechou sem enviar, registra dismiss
    axios.post(`${API}/nudge/dismiss`, { type: "review" }, { withCredentials: true }).catch(() => {});
  };

  return (
    <>
      {showReview && <ReviewModal onClose={handleReviewClose} />}

      {nudge && visible && (
        <div className="fixed bottom-6 right-6 z-50 w-80 bg-white border border-[#D1DEEE] rounded-xl animate-fade-in-1">
          <button
            onClick={dismiss}
            className="absolute top-3 right-3 text-[#335480]/50 hover:text-[#335480] transition-colors"
            aria-label="Fechar"
          >
            <X size={16} />
          </button>

          {nudge === "share" && (
            <div className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Share2 size={18} className="text-[#003882]" />
                <span className="font-bold text-[#001533] text-sm">Indique o TUTO</span>
              </div>
              <p className="text-[#335480] text-sm mb-4">
                Conhece outro advogado que sofre com intimações perdidas? Compartilhe o TUTO com ele.
              </p>
              <div className="flex gap-2">
                <a
                  href={`https://wa.me/?text=${WHATSAPP_TEXT}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={dismiss}
                  className="flex-1 h-9 flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#1fbd59] text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  WhatsApp
                </a>
                <button
                  onClick={handleShare}
                  className="flex-1 h-9 border border-[#D1DEEE] text-[#335480] hover:bg-[#F4F7FB] text-sm font-medium rounded-lg transition-colors"
                >
                  {copied ? "Copiado! ✓" : "Copiar link"}
                </button>
              </div>
              <button onClick={dismiss} className="w-full mt-2 text-xs text-[#335480]/50 hover:text-[#335480] text-center">
                Agora não
              </button>
            </div>
          )}

          {nudge === "review" && (
            <div className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Star size={18} className="text-[#f59e0b] fill-[#f59e0b]" />
                <span className="font-bold text-[#001533] text-sm">O que está achando do TUTO?</span>
              </div>
              <p className="text-[#335480] text-sm mb-4">
                Sua opinião nos ajuda a melhorar. Leva menos de 1 minuto.
              </p>
              <button
                onClick={handleReviewOpen}
                className="w-full h-9 flex items-center justify-center gap-1.5 bg-[#003882] hover:bg-[#00285e] text-white text-sm font-semibold rounded-lg transition-colors"
              >
                <Star size={14} className="fill-white" /> Avaliar agora
              </button>
              <button onClick={dismiss} className="w-full mt-2 text-xs text-[#335480]/50 hover:text-[#335480] text-center">
                Agora não
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
